import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DublettenTreffer, KundeStamm, Unternehmen,
              FieldSuggestion, LogoCandidate,
              KundeAnlegen as KundeAnlegenEingabe } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, useBeforeUnload, useFormularZustand, useBestaetigung }
  from "@kundeninfo/ui";
import { Auswahl, Bildkachel, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../router.js";
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "../components/meHuellen.js";
import "./KundeAnlegen.css";

/** Nur die Klientenmethoden, die diese Maske wirklich braucht — so lässt sie sich im Test
 *  mit wenigen vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen. lookupEnrichment und
 *  uploadCustomerLogo kamen mit der Anreicherung dazu (Aufgabe 7). */
export type AnlegenClient =
  Pick<ApiClient, "findDuplicates" | "createCustomer" | "addRelation"
    | "lookupEnrichment" | "uploadCustomerLogo">;

export type KundeAnlegenProps = {
  client: AnlegenClient;
  /** Für die Unternehmens-Auswahl (sitzung.companies). */
  companies: Unternehmen[];
  /** Vorbelegung: das Unternehmen des Benutzers (sitzung.user.homeCompanyId). */
  homeCompanyId: string | null;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7) — geht der Heimat
   *  vor. `null` oder nicht zur Auswahl: Bestand (Heimat, sonst das erste). */
  arbeitsunternehmenId: string | null;
  /** Nach dem Anlegen ODER dem Ergänzen einer Beziehung: die Akte dieses Kunden öffnen. */
  onFertig(id: string): void;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
};

/** Ab so vielen Zeichen sucht die Maske nach bestehenden Firmen. */
const MIN_SUCHLAENGE = 3;
/** Entprellung der Dublettensuche — ohne sie eine Abfrage je Tastendruck. */
const SUCH_VERZOEGERUNG_MS = 300;
/** Entprellung des Anreicherungs-Lookups — dasselbe Idiom wie die Dublettensuche, nur
 *  etwas länger, weil dahinter fremde Quellen (VIES, Impressum, Webseite) hängen. */
const ANREICHERUNG_VERZOEGERUNG_MS = 500;

/** Die Quelle eines Vorschlags, für den Bearbeiter benannt. */
const QUELLE_LABEL: Record<FieldSuggestion["source"], string> = {
  VIES: "VIES", IMPRESSUM: "Impressum", WEBSITE: "Webseite", CREDITREFORM: "Creditreform",
};

/** Wert der Kachel „ohne Logo“ in der Bildkachel — keine URL, deshalb nie mit einem
 *  Vorschlag verwechselbar. */
const OHNE_LOGO = "ohne-logo";

/** Die Formularwerte des Firmenblocks — überall Zeichenketten; leer wird erst beim
 *  Absenden zu null (wie in KundeStammdaten.tsx). */
type Entwurf = {
  companyName: string; street: string; houseNumber: string; postalCode: string;
  city: string;
  phone: string; email: string; vatId: string; industry: string; website: string;
};

const LEERER_ENTWURF: Entwurf = {
  companyName: "", street: "", houseNumber: "", postalCode: "", city: "",
  phone: "", email: "", vatId: "", industry: "", website: "",
};

// Die Webadresse steht zwischen E-Mail und Branche: Sie gehört zu den Kontaktwegen, und
// sie ist der Ausgangspunkt der späteren Anreicherung (Impressum, Logo). Ohne Schema
// getippt („example.de") ergänzt der Server https:// (normalizeWebsite in domain) — die
// Maske schreibt bewusst nichts um, sonst stünden zwei Stellen für dieselbe Regel.
const FELDER: { name: keyof Entwurf; label: string; typ?: "email" | "tel" }[] = [
  { name: "street", label: "Straße" },
  { name: "houseNumber", label: "Hausnummer" },
  { name: "postalCode", label: "PLZ" },
  { name: "city", label: "Ort" },
  { name: "phone", label: "Telefon", typ: "tel" },
  { name: "email", label: "E-Mail", typ: "email" },
  { name: "website", label: "Webseite" },
  { name: "vatId", label: "USt-IdNr." },
  { name: "industry", label: "Branche" },
];

type Status = KundeAnlegenEingabe["status"];

const STATUS_WERTE: { wert: Status; label: string }[] = [
  { wert: "PROSPECT", label: "Interessent" },
  { wert: "ACTIVE", label: "Aktiv" },
  { wert: "DORMANT", label: "Ruhend" },
  { wert: "FORMER", label: "Ehemalig" },
];

/** Alle Feldnamen, die eine 422 nennen kann, auf die Eingaben dieser Maske abgebildet. */
type EingabeName = keyof Entwurf | "customerNumber" | "companyId" | "status" | "customerSince";

const EINGABE_NAMEN: EingabeName[] = [
  "companyName", ...FELDER.map((f) => f.name),
  "customerNumber", "companyId", "status", "customerSince",
];

function oderNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert.trim();
}

function stammAus(entwurf: Entwurf): KundeStamm {
  return {
    companyName: entwurf.companyName,
    street: oderNull(entwurf.street),
    houseNumber: oderNull(entwurf.houseNumber),
    postalCode: oderNull(entwurf.postalCode),
    city: oderNull(entwurf.city),
    phone: oderNull(entwurf.phone),
    email: oderNull(entwurf.email),
    vatId: oderNull(entwurf.vatId),
    industry: oderNull(entwurf.industry),
    website: oderNull(entwurf.website),
  };
}

function ortVon(t: DublettenTreffer): string {
  return [t.postalCode, t.city]
    .filter((teil): teil is string => teil !== null && teil !== "")
    .join(" ");
}

/**
 * Die Maske hinter /kunden/neu. Ihre Reihenfolge ist Absicht: ZUERST der Firmenname —
 * ab drei Zeichen sucht die Maske (entprellt) über ALLE Unternehmen nach bestehenden
 * Firmen und lenkt bei einem Treffer auf „Beziehung ergänzen" statt auf eine zweite
 * Firmenzeile. Verboten wird nichts: Wer sicher ist, dass es eine andere Firma ist,
 * legt trotzdem an (siehe Spezifikation „Einen Kunden anlegen").
 *
 * Das Nummernfeld bleibt leer und trägt nur den Hinweis, dass die Nummer beim Speichern
 * vergeben wird — KEIN Vorschlag beim Öffnen: Zwei gleichzeitig offene Formulare
 * bekämen denselben, und jedes abgebrochene Formular verbrännte eine Nummer. Eine
 * bestehende Nummer (Portal-Mandant, später DATEV-Debitor) wird von Hand eingetragen.
 */
export function KundeAnlegen(p: KundeAnlegenProps): ReactElement {
  const [entwurf, setEntwurf] = useState<Entwurf>(LEERER_ENTWURF);
  const [companyId, setCompanyId] = useState<string>(() =>
    p.companies.find((c) => c.id === p.arbeitsunternehmenId)?.id
      ?? p.companies.find((c) => c.id === p.homeCompanyId)?.id ?? p.companies[0]?.id ?? "");
  const [nummer, setNummer] = useState("");
  const [status, setStatus] = useState<Status>("PROSPECT");
  const [seit, setSeit] = useState("");

  const [treffer, setTreffer] = useState<DublettenTreffer[]>([]);
  /** Nur noch der Hinweis „angelegt, aber das Logo nicht“ — Befunde des Anlegens selbst
   *  führt `formular.meldung`. */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** WELCHER der zwei Schreibwege `formular.aktion()` gleich ausführt — Anlegen oder das
   *  Ergänzen einer Beziehung am Dubletten-Treffer. Beide teilen Felder, Befunde und Sperre. */
  const auftrag = useRef<() => Promise<{ id: string; logoFehler: string | null }>>(
    async () => ({ id: "", logoFehler: null }));

  // Anreicherung (Aufgabe 7): Vorschläge und Logo-Kandidaten aus dem Lookup. Der Dienst
  // schreibt nie selbst — diese Werte werden Feld für Feld ausdrücklich übernommen.
  const [vorschlaege, setVorschlaege] = useState<FieldSuggestion[]>([]);
  const [logoKandidaten, setLogoKandidaten] = useState<LogoCandidate[]>([]);
  /** Die im Logo-Dialog bestätigte URL — erst NACH dem Anlegen an die Logo-Route gereicht. */
  const [gewaehltesLogo, setGewaehltesLogo] = useState<string | null>(null);
  /** Der Kandidat, dessen Markenrechts-Dialog gerade offen ist (null = kein Dialog). */
  const [logoDialog, setLogoDialog] = useState<string | null>(null);
  /** Gesetzt, wenn der Kunde ANGELEGT ist, das gewählte Logo aber scheiterte: Dann wird
   *  NICHT automatisch navigiert (sonst sähe niemand die Meldung), sondern der Bearbeiter
   *  springt selbst mit „Zur Kundenakte" in die Akte (fertig(id)). Kein Rollback. */
  const [angelegtId, setAngelegtId] = useState<string | null>(null);

  /** Die Vorbelegung des Unternehmens — nur eine Abweichung davon zählt als Änderung. */
  const startCompanyId = useRef(companyId);

  // Die Dublettensuche: entprellt, und der laufende Timer wird bei jeder weiteren
  // Eingabe wie beim Verlassen der Maske aufgeräumt (die Effekt-Aufräumfunktion läuft
  // in beiden Fällen). `abgebrochen` fängt zusätzlich eine schon abgeschickte Antwort
  // ab, die erst nach dem Verlassen einträfe.
  const suchName = entwurf.companyName.trim();
  const suche = p.client.findDuplicates;
  useEffect(() => {
    if (suchName.length < MIN_SUCHLAENGE) { setTreffer([]); return; }
    let abgebrochen = false;
    const timer = setTimeout(() => {
      suche(suchName)
        .then((antwort) => { if (!abgebrochen) setTreffer(antwort.items); })
        // Die Suche ist Lenkung, kein Pflichtweg: Scheitert sie (Netz weg), bleibt die
        // Maske still bedienbar, statt das Anlegen mit einer Fehlermeldung zu verstellen.
        .catch(() => {});
    }, SUCH_VERZOEGERUNG_MS);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [suchName, suche]);

  // Der Anreicherungs-Lookup — bewusst nach demselben Muster wie die Dublettensuche oben:
  // entprellt, mit `abgebrochen`-Flag gegen eine veraltete Antwort und `.catch(()=>{})`,
  // damit eine kranke Quelle die Maske NIE verstellt und das Anlegen NIE blockiert.
  // Auslöser ist die USt-IdNr ODER die Webseite (der Firmenname allein löst nicht aus —
  // keine Quelle fängt damit etwas an); ist ein Firmenname ≥ 3 Zeichen da, geht er als
  // zusätzlicher Hinweis mit.
  const vatTrim = entwurf.vatId.trim();
  const webTrim = entwurf.website.trim();
  const nameTrim = entwurf.companyName.trim();
  const anreichern = p.client.lookupEnrichment;
  useEffect(() => {
    if (vatTrim === "" && webTrim === "") { setVorschlaege([]); setLogoKandidaten([]); return; }
    let abgebrochen = false;
    const timer = setTimeout(() => {
      const hinweis: { vatId?: string; companyName?: string; website?: string } = {};
      if (vatTrim !== "") hinweis.vatId = vatTrim;
      if (webTrim !== "") hinweis.website = webTrim;
      if (nameTrim.length >= MIN_SUCHLAENGE) hinweis.companyName = nameTrim;
      anreichern(hinweis)
        .then((antwort) => {
          if (abgebrochen) return;
          setVorschlaege(antwort.suggestions);
          setLogoKandidaten(antwort.logoCandidates);
        })
        .catch(() => {});
    }, ANREICHERUNG_VERZOEGERUNG_MS);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [vatTrim, webTrim, nameTrim, anreichern]);

  // Verlassen-Wächter: registriert, sobald irgendeine Eingabe von der leeren Maske
  // abweicht — eine halb ausgefüllte Anlage-Maske ist genau der Fall, für den er gebaut
  // wurde. `abmeldenRef` erlaubt dem Erfolgsweg, den Wächter VOR dem eigenen Sprung in
  // die Akte abzumelden — die Effekt-Aufräumung liefe erst nach der Navigation.
  const geaendert =
    entwurf.companyName !== "" || FELDER.some((f) => entwurf[f.name] !== "")
    || nummer !== "" || seit !== "" || status !== "PROSPECT"
    || companyId !== startCompanyId.current;
  const abmeldenRef = useRef<(() => void) | null>(null);
  const { bestaetigen } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!geaendert) return;
    const abmelden = registriere(() => bestaetigen({
      titel: "Seite verlassen?",
      text: "Es gibt ungespeicherte Änderungen. Sie gehen verloren.",
      bestaetigenText: "Verlassen",
      gefaehrlich: true,
    }));
    abmeldenRef.current = abmelden;
    return () => { abmeldenRef.current = null; abmelden(); };
  }, [geaendert, registriere]);

  // …und derselbe Stand für das ECHTE Verlassen (Tab zu, neu laden, App-Wechsel), das
  // der Wächter oben nicht sieht (2a-Kleinpunkt).
  useBeforeUnload(geaendert);

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3): Während des Anlegens sind alle Felder
   * gesperrt (readOnly + aria-disabled, der Fokus bleibt) und der Knopf steht; eine 422
   * hängt am genannten Feld — auch an den Auswahlen Unternehmen/Status — und der Fokus
   * springt dorthin; ein Befund zu einem Feld, das diese Maske nicht zeigt, steht in der
   * Meldung (nie unsichtbar). Anlegen kennt keine Version, also keine 409.
   */
  const formular = useFormularZustand({
    speichern: () => auftrag.current(),
    neuLaden: async () => { /* keine 409 beim Anlegen */ },
    bekannteFelder: EINGABE_NAMEN,
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<EingabeName, string>>;

  /** Die Beziehungswerte (rechte Hälfte der Maske) — identisch fürs Anlegen wie fürs
   *  Ergänzen am bestehenden Kunden. */
  function beziehungsWerte(): { companyId: string; customerNumber: string | null;
    status: Status; customerSince: string | null } {
    return { companyId, customerNumber: oderNull(nummer), status, customerSince: oderNull(seit) };
  }

  /** Der Erfolgsweg beider Aktionen: Wächter abmelden, dann in die Akte springen. */
  function fertig(id: string): void {
    abmeldenRef.current?.();
    p.onFertig(id);
  }

  async function anlegen(): Promise<void> {
    setMeldung(null);
    auftrag.current = async () => {
      const antwort = await p.client.createCustomer(
        { customer: stammAus(entwurf), ...beziehungsWerte() });
      // Reihenfolge Anlegen → Logo: die Logo-Route braucht die frisch vergebene Kunden-Id.
      // Scheitert das Logo, BLEIBT der Kunde bestehen (kein Rollback) — und das ist KEIN
      // Fehler des Anlegens: Er geht als Ergebnis zurück, nicht als Ausnahme.
      if (gewaehltesLogo !== null) {
        try {
          await p.client.uploadCustomerLogo(antwort.id, gewaehltesLogo);
        } catch (logoFehler) {
          const grund = logoFehler instanceof ApiError ? logoFehler.message : "unbekannter Fehler";
          return { id: antwort.id, logoFehler: grund };
        }
      }
      return { id: antwort.id, logoFehler: null };
    };
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined) return; // Befund am Feld oder in der Meldung
    if (ergebnis.logoFehler !== null) {
      // NICHT automatisch navigieren — die Navigation hängt die Maske aus, und die Meldung
      // wäre nie zu sehen (Abschlussfund W1). Stattdessen bleibt die Maske stehen, zeigt
      // die Meldung und einen ausdrücklichen Knopf „Zur Kundenakte".
      setMeldung("Der Kunde wurde angelegt, das Logo konnte aber nicht übernommen werden: "
        + ergebnis.logoFehler);
      setAngelegtId(ergebnis.id);
      return;
    }
    fertig(ergebnis.id);
  }

  /** Die Lenkung: dem BESTEHENDEN Kunden eine Beziehung zum eigenen Unternehmen
   *  ergänzen, statt eine zweite Firmenzeile anzulegen. */
  async function beziehungErgaenzen(t: DublettenTreffer): Promise<void> {
    setMeldung(null);
    auftrag.current = async () => {
      await p.client.addRelation(t.id, beziehungsWerte());
      return { id: t.id, logoFehler: null };
    };
    const ergebnis = await formular.aktion();
    if (ergebnis !== undefined) fertig(ergebnis.id);
  }

  /** Das aktuell im Beziehungsblock gewählte Unternehmen — bestimmt, ob ein
   *  Dubletten-Treffer dort schon Kunde ist (siehe Kommentar in der Trefferliste). */
  const gewaehlteFirma = p.companies.find((c) => c.id === companyId);

  /** Die Vorschlagsleiste zu einem Feld: Wert, Quelle und ein Übernehmen-Knopf, der SICHTBAR
   *  und nur dieses eine Feld setzt. Trägt das Feld den Vorschlag schon, steht statt des
   *  Knopfs „übernommen" — nichts wird je still ersetzt. */
  function vorschlagsLeiste(feld: keyof Entwurf, label: string): ReactElement | null {
    const v = vorschlaege.find((s) => s.field === feld);
    if (v === undefined) return null;
    const schonUebernommen = entwurf[feld] === v.value;
    return (
      <div className="ka__vorschlag" role="group" aria-label={`Vorschlag für ${label}`}>
        <span className="ka__vorschlag-wert">{v.value}</span>
        <span className="ka__vorschlag-quelle">{QUELLE_LABEL[v.source]}</span>
        {schonUebernommen
          ? <span className="ka__vorschlag-uebernommen">übernommen</span>
          : (
            <KiKnopf className="ka__vorschlag-knopf"
              onClick={() => setEntwurf((e) => ({ ...e, [feld]: v.value }))}>
              Übernehmen
            </KiKnopf>
          )}
      </div>
    );
  }

  /** Ein Feld samt Vorschlagsleiste als eine Rasterzelle — so sitzt der Vorschlag UNTER dem
   *  Feld, ohne das Tippen zu stören (kein Layout-Sprung, der den Fokus verlöre). */
  function feldMitVorschlag(feld: keyof Entwurf, unten?: ReactElement | null): ReactElement {
    const f = FELDER.find((x) => x.name === feld);
    const fehlerText = feldFehler[feld];
    return (
      <div className="ka__feld-mit-vorschlag" key={feld}>
        <Feld id={`ka-${feld}`} label={f?.label ?? feld} wert={entwurf[feld]} bearbeitbar
          onChange={(wert) => setEntwurf((e) => ({ ...e, [feld]: wert }))}
          {...(f?.typ !== undefined ? { typ: f.typ } : {})}
          {...(fehlerText !== undefined ? { fehler: fehlerText } : {})}
        />
        {vorschlagsLeiste(feld, f?.label ?? feld)}
        {unten}
      </div>
    );
  }

  /** Der VIES-Gültigkeitsbefund — ein Hinweis neben der USt-IdNr, KEIN übernehmbarer Wert. */
  // Der VIES-Gültigkeitsbefund als grüner Haken (Befund Dirk 01.09.): unmissverständlich
  // sichtbar, dass die USt-IdNr laut VIES gültig ist — auch wenn (wie bei deutschen Nummern)
  // KEIN Firmenname mitkommt, weil Deutschland ihn über VIES nicht herausgibt. role="status"
  // trägt den Text als zugängliche Bezeichnung; der Haken selbst ist aria-hidden.
  const viesBefund = vorschlaege.find((s) => s.field === "vatIdValid");
  const viesHinweis = viesBefund === undefined ? null : viesBefund.value === "true" ? (
    <span className="ka__vies-gueltig" role="status" aria-label="laut VIES gültig">
      <span aria-hidden="true">✓</span> laut VIES gültig
    </span>
  ) : (
    <span className="ka__vies-ungueltig" role="status" aria-label="laut VIES ungültig">
      <span aria-hidden="true">✗</span> laut VIES ungültig
    </span>
  );


  return (
    // Eigene Wurzelklasse (NICHT "ka"): Die Kundenakte (KundenAkte.tsx) benutzt ebenfalls
    // ".ka", und deren globale Regeln (bzw. hier max-width: 560px) trügen sonst über beide
    // Ansichten (Befund Dirk 01.09.2026 — die Akte war dadurch auf 560px gedeckelt, die
    // Tab-Leiste konnte die Breite nie nutzen).
    <div className="ka-neu">
      <h2 className="ka-neu__titel">Neuen Kunden anlegen</h2>
      <p className="ka__anreicherung-hinweis">
        Sobald eine <strong>USt-IdNr.</strong> oder eine <strong>Webseite</strong> eingetragen
        ist, schlägt das System dazu Stammdaten und ein Logo vor (aus VIES, Impressum und der
        Webseite) — ganz ohne Knopf. Die Vorschläge übernimmst du einzeln; nichts wird
        automatisch gesetzt. Der Firmenname allein löst noch keine Suche aus.
      </p>

      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}

      {/* Das Formular (me-formular) trägt die Zustände: Sperre während des Anlegens,
          Sprung zum ersten Fehler. Beschriftung „oben": Die Felder bringen ihre eigene
          Beschriftung mit; das Raster je Abschnitt (zwei Spalten ab Tablet) bleibt
          `ka__felder` — jeder Abschnitt ist EINE Zeile des Formulars. */}
      <Formular beschriftung="oben" {...formular.formularProps}>
      <div className="ka-neu__formular">
      {/* Zuerst der Firmenname — er treibt die Dublettensuche. */}
      <div className="ka__felder">
        <div className="ka__feld-mit-vorschlag">
          <Feld id="ka-companyName" label="Firmenname" wert={entwurf.companyName}
            bearbeitbar
            onChange={(wert) => setEntwurf((e) => ({ ...e, companyName: wert }))}
            {...(feldFehler.companyName !== undefined ? { fehler: feldFehler.companyName } : {})}
          />
          {vorschlagsLeiste("companyName", "Firmenname")}
        </div>
      </div>

      {treffer.length > 0 && (
        <section className="ka__dubletten" aria-label="Mögliche Dubletten">
          <h3 className="ka__dubletten-titel">Gibt es diese Firma schon?</h3>
          <p className="ka__dubletten-text">
            Ähnlich benannte Firmen sind bereits erfasst. Ergänze dort eine Beziehung für
            dein Unternehmen, statt die Firma ein zweites Mal anzulegen — Nummer, Status
            und „Kunde seit" aus dieser Maske werden dabei übernommen.
          </p>
          <ul className="ka__dubletten-liste">
            {treffer.map((t) => {
              // Trägt der Treffer bereits das Kürzel des GEWÄHLTEN Unternehmens, gibt es
              // dort nichts zu ergänzen — der Server wiese den Versuch ohnehin ab
              // (uq_cr_pair, „Diese Firma ist bei diesem Unternehmen bereits Kunde").
              // Die Maske soll aber nicht in eine sichere Ablehnung lenken: Der Knopf
              // sagt stattdessen, WARUM es nichts zu tun gibt, und ist stillgelegt
              // (Aufgabe 17, Sammelrunde). Wechselt die Auswahl unten auf ein anderes
              // Unternehmen, lebt er wieder auf.
              const bereitsKundeBei = gewaehlteFirma !== undefined
                && t.relationShortCodes.includes(gewaehlteFirma.shortCode)
                ? gewaehlteFirma.shortCode : null;
              return (
                <li key={t.id} className="ka__dublette">
                  <span className="ka__dublette-name">{t.companyName}</span>
                  {ortVon(t) !== "" && (
                    <span className="ka__dublette-ort">{ortVon(t)}</span>
                  )}
                  {/* Absichtlich nur die Kürzel: Die Suche geht über ALLE Unternehmen und
                      liefert deshalb nur die TATSACHE einer Beziehung, keine fremden
                      Nummern, Stati oder Konditionen (siehe Spezifikation). */}
                  <span className="ka__dublette-chips">
                    {t.relationShortCodes.map((kuerzel) => (
                      <Chip key={kuerzel} className="ka__chip"
                        {...hausfarbeChip(p.companies, kuerzel)}>{kuerzel}</Chip>
                    ))}
                  </span>
                  <KiKnopf className="ka__dublette-knopf"
                    deaktiviert={arbeitet || bereitsKundeBei !== null}
                    onClick={() => { void beziehungErgaenzen(t); }}>
                    {bereitsKundeBei !== null
                      ? `Bereits Kunde bei ${bereitsKundeBei}`
                      : "Beziehung ergänzen"}
                  </KiKnopf>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="ka__felder">
        {FELDER.map((f) =>
          // industry trägt keinen Vorschlag; der VIES-Befund hängt an der USt-IdNr.
          feldMitVorschlag(f.name, f.name === "vatId" ? viesHinweis : null))}
      </div>

      {(logoKandidaten.length > 0 || gewaehltesLogo !== null) && (
        // Bildkachel (me-design a9a3e5a) statt des Übergangs P12-4. Kontrolliert: Ein
        // Vorschlag wird erst nach „Übernehmen“ im Markenrechts-Dialog zur Wahl — bis dahin
        // setzt das Element die Kachel auf die bisherige Wahl zurück. „ohne Logo“ gilt sofort.
        <Bildkachel name="ka-logo" beschriftung="Logo-Vorschläge"
          hilfe="Zur Übernahme ein Bild wählen — es wird erst nach dem Anlegen des Kunden geladen."
          wert={gewaehltesLogo ?? OHNE_LOGO}
          onGeaendert={(d) => {
            if (d.art === "keins") setGewaehltesLogo(null);
            else setLogoDialog(d.wert);
          }}>
          <option value={OHNE_LOGO} data-art="keins" data-bildtext="ohne Logo" data-quelle="">Kein Logo</option>
          {logoKandidaten.map((k, i) => (
            <option key={k.url} value={k.url} data-bild={k.url} data-quelle={QUELLE_LABEL[k.source]}>
              {`Vorschlag ${i + 1}`}
            </option>
          ))}
          {/* Eine schon übernommene Wahl, die ein neuer Lookup nicht mehr vorschlägt, bleibt
              sichtbar — sie wird beim Anlegen weiter übernommen. */}
          {gewaehltesLogo !== null && !logoKandidaten.some((k) => k.url === gewaehltesLogo) && (
            <option value={gewaehltesLogo} data-bild={gewaehltesLogo}>Übernommen</option>
          )}
        </Bildkachel>
      )}

      <h3 className="ka__zwischentitel">Kundenbeziehung</h3>
      <div className="ka__felder">
        <Auswahl id="ka-companyId" name="ka-companyId" beschriftung="Unternehmen"
          wert={companyId}
          {...(feldFehler.companyId !== undefined ? { fehler: feldFehler.companyId } : {})}
          onGeaendert={(d) => setCompanyId(d.wert)}>
          {p.companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Auswahl>

        <Feld id="ka-customerNumber" label="Kundennummer" wert={nummer} bearbeitbar
          onChange={setNummer} fehler={feldFehler.customerNumber}
          hilfe={"Leer lassen — die Nummer wird beim Speichern vergeben. Eine bestehende "
            + "Nummer (z. B. aus dem Portal) trägst du hier ein."} />

        <Auswahl id="ka-status" name="ka-status" beschriftung="Status" wert={status}
          {...(feldFehler.status !== undefined ? { fehler: feldFehler.status } : {})}
          onGeaendert={(d) => setStatus(d.wert as Status)}>
          {STATUS_WERTE.map((s) => (
            <option key={s.wert} value={s.wert}>{s.label}</option>
          ))}
        </Auswahl>

        <Feld id="ka-customerSince" label="Kunde seit" wert={seit} bearbeitbar typ="date"
          onChange={setSeit}
          {...(feldFehler.customerSince !== undefined
            ? { fehler: feldFehler.customerSince } : {})}
        />
      </div>
      </div>
      </Formular>

      <div className="ka__aktionen">
        {angelegtId !== null ? (
          // Der Kunde ist angelegt, nur das Logo scheiterte: kein zweites Anlegen (das
          // erzeugte eine Dublette), sondern der ausdrückliche Sprung in die Akte.
          <KiKnopf art="primaer" className="ka__anlegen"
            onClick={() => { fertig(angelegtId); }}>
            Zur Kundenakte
          </KiKnopf>
        ) : (
          <KiKnopf art="primaer" className="ka__anlegen" deaktiviert={arbeitet}
            onClick={() => { void anlegen(); }}>
            {treffer.length > 0 ? "Trotzdem neu anlegen" : "Kunde anlegen"}
          </KiKnopf>
        )}
      </div>

      {logoDialog !== null && (
        <Modal titel="Logo übernehmen" onAbbrechen={() => setLogoDialog(null)}>
          <div className="ka__dialoginhalt">
            <img className="ka__dialog-bild" src={logoDialog} alt="" />
            {/* Markenrechts-Hinweis — Wortlaut EXAKT aus dem Fundament (Abschnitt „Logos
                sind geschützt"), nicht kürzen und nicht umformulieren. */}
            <p className="ka__dialog-hinweis">
              Ein Firmenlogo ist Marke und urheberrechtlich geschützt. Die Verwendung zur
              Wiedererkennung in der internen Kundenakte ist vertretbar; eine Verwendung nach
              außen wäre es nicht.
            </p>
          </div>
          {/* Eine Wahl ohne Eingabe: Es gibt keinen Entwurf, den ein Abbrechen verlöre —
              deshalb fragt es hier nicht nach (Hausregel „striktes Modalpanel"). */}
          <ModalFuss speichernText="Übernehmen"
            onSpeichern={() => { setGewaehltesLogo(logoDialog); setLogoDialog(null); }}
            onAbbrechen={() => setLogoDialog(null)} />
        </Modal>
      )}
    </div>
  );
}
